import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import {
  Users, DollarSign, TrendingUp, Target, Briefcase, Loader2, ChevronRight,
  BarChart3, Activity, PieChart as PieChartIcon,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v || 0);
const fmtCompact = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(v || 0);
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

function StatCard({ label, value, icon: Icon, accent = 'cyan', sublabel }) {
  const accents = {
    cyan: 'bg-cyan-50 text-cyan-600',
    green: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-semibold mt-1">{value}</p>
          {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accents[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function PraxisDashboardPage() {
  const { currentCompany } = useCompany();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/praxis/dashboard`, {
        params: { company_id: currentCompany.id },
      });
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!currentCompany) return null;
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const empty = !data || data.total_clients === 0;
  if (empty) {
    return (
      <div className="space-y-6" data-testid="praxis-dashboard">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Praxis Dashboard</h1>
          <p className="text-gray-500 mt-1">Aggregated overview across all Praxis clients</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No clients yet</h3>
          <p className="text-gray-500 mb-4">Add your first client to start tracking performance.</p>
          <Link
            to="/praxis/clients"
            className="inline-flex items-center px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-medium"
          >
            Go to Clients <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </div>
    );
  }

  const top = data.per_client.slice(0, 10);
  const statusData = [
    { name: 'Active', value: data.status_split.active || 0 },
    { name: 'Inactive', value: data.status_split.inactive || 0 },
    { name: 'Closed', value: data.status_split.closed || 0 },
  ].filter((x) => x.value > 0);
  const COLORS = ['#06B6D4', '#A1A1AA', '#EF4444'];

  return (
    <div className="space-y-6" data-testid="praxis-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Praxis Dashboard</h1>
          <p className="text-gray-500 mt-1">Aggregated overview across all Praxis clients</p>
        </div>
        <Link
          to="/praxis/clients"
          className="text-sm font-medium text-cyan-600 hover:text-cyan-700 flex items-center"
        >
          Manage clients <ChevronRight className="w-4 h-4 ml-1" />
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Clients"
          value={data.total_clients}
          icon={Users}
          accent="cyan"
          sublabel={`${data.active_clients} active`}
        />
        <StatCard label="Total AUM" value={fmtCompact(data.total_aum)} icon={DollarSign} accent="green" />
        <StatCard label="Contract Total" value={fmtCompact(data.total_contract_amount)} icon={Briefcase} accent="purple" />
        <StatCard label="Average Return" value={`${data.average_return}%`} icon={TrendingUp} accent="amber" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-cyan-600" />
            <h2 className="font-medium text-gray-900">AUM by Client (Top {Math.min(10, top.length)})</h2>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={top} margin={{ top: 5, right: 16, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="client_name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                <Tooltip formatter={(v) => fmtCurrency(v)} />
                <Bar dataKey="aum" name="AUM" radius={[4, 4, 0, 0]}>
                  {top.map((_, i) => <Cell key={i} fill="#06B6D4" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <PieChartIcon className="w-4 h-4 text-cyan-600" />
            <h2 className="font-medium text-gray-900">Status Split</h2>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={(d) => d.value}>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Returns vs Target */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-100 flex items-center gap-2">
          <Target className="w-4 h-4 text-cyan-600" />
          <h2 className="font-medium text-gray-900">Returns vs Target</h2>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={top} margin={{ top: 5, right: 16, left: 0, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="client_name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Legend />
              <Bar dataKey="current_return" name="Current" fill="#06B6D4" radius={[4, 4, 0, 0]} />
              <Bar dataKey="target_return" name="Target" fill="#A1A1AA" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Client list */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-600" />
            <h2 className="font-medium text-gray-900">Clients</h2>
          </div>
          <Link to="/praxis/clients" className="text-xs font-medium text-cyan-600 hover:text-cyan-700">
            View all →
          </Link>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Contract</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">AUM</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Target</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.per_client.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-sm">
                  <Link to={`/praxis/clients/${c.id}`} className="font-medium text-cyan-700 hover:underline">
                    {c.client_name}
                  </Link>
                </td>
                <td className="px-4 py-2.5">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.status === 'active'
                        ? 'bg-emerald-100 text-emerald-700'
                        : c.status === 'inactive'
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {c.status}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-sm text-right text-gray-900">{fmtCurrency(c.contract_amount)}</td>
                <td className="px-4 py-2.5 text-sm text-right text-gray-900 font-medium">{fmtCurrency(c.aum)}</td>
                <td className="px-4 py-2.5 text-sm text-right text-cyan-700 font-medium">{fmtPct(c.current_return)}</td>
                <td className="px-4 py-2.5 text-sm text-right text-gray-500">{fmtPct(c.target_return)}</td>
                <td className="px-4 py-2.5 text-sm text-gray-600 capitalize">{c.payment_schedule || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
